import { useTestVariables } from '~/hooks/test/useTestVariables';
import { api } from '~/trpc-extra/api';
import { useForm } from '@mantine/form';
import { ParameterizedInput } from '../../ParameterizedInput';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import { notifications } from '@mantine/notifications';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';
import { EditableBadge } from '~/components/common/EditableBadge';
import { Text } from '@mantine/core';

export function ComponentParameters({
  componentCallOp,
  testContentId,
  operationId,
  parentContext,
}: {
  componentCallOp: SelectedOperation['componentCallOp'];
  testContentId: string;
  operationId: string;
  parentContext?: OperationParentContext;
}) {
  const trpcUtils = api.useUtils();
  const { mutateAsync: updateComponentCallOp } = api.component.updateComponentCallOp.useMutation();
  const testVariables = useTestVariables({
    testContentId,
    limit: { type: 'operationId', id: operationId },
  });

  const { mutateAsync: updateComponent } = api.component.update.useMutation({
    onSuccess: () => {
      if (parentContext?.componentId) {
        void trpcUtils.component.get.invalidate({ id: parentContext.componentId });
      } else if (parentContext?.conditional) {
        void trpcUtils.operation.getFullConditionalBlock.invalidate({
          conditionalBlockId: parentContext.conditional.conditionalBlockId,
        });
      } else {
        void trpcUtils.testContent.get.invalidate({ id: testContentId });
      }
    },
    onError: (error) => {
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red',
      });
    },
  });

  const form = useForm({
    initialValues: {
      inputParameters: componentCallOp?.inputParameters || {},
    },
  });

  const hasParams = !!componentCallOp?.component?.parameters?.length;
  const hasOutputVars = !!componentCallOp?.component?.outputVariables?.length;

  const handleParameterNameChange = async (oldName: string, newName: string) => {
    if (oldName === newName) {
      return;
    }

    const existingParameters = componentCallOp?.component.parameters || [];
    if (existingParameters.includes(newName)) {
      notifications.show({
        title: 'Duplicate Parameter Name',
        message: `A parameter with the name "${newName}" already exists. Please choose a different name.`,
        color: 'red',
      });
      return;
    }

    const newParameters: string[] = existingParameters.map((param) => (param === oldName ? newName : param));

    await updateComponent({
      id: componentCallOp?.componentId || '',
      update: { parameters: newParameters },
    });

    const currentInputParams = form.values.inputParameters;

    // Update the input parameters to the new name
    if (currentInputParams[oldName]) {
      const { [oldName]: oldValue, ...restParams } = currentInputParams;
      const updatedInputParams = {
        ...restParams,
        [newName]: oldValue,
      };
      form.setValues({ inputParameters: updatedInputParams });
    }
  };

  return (
    <div className="space-y-4 bg-gray-50 px-3 dark:bg-neutral-700/20">
      {!!hasParams && (
        <div className="flex flex-col gap-2 pb-2">
          {componentCallOp.component.parameters?.map((parameterName) => {
            const currentParam = form.values.inputParameters?.[parameterName] || { type: 'STATIC', value: '' };

            return (
              <div
                key={parameterName}
                className="flex items-end gap-2"
              >
                <ParameterizedInput
                  label={
                    <EditableBadge
                      label={parameterName}
                      onChange={handleParameterNameChange}
                    />
                  }
                  className="min-w-0 flex-1"
                  placeholder="Enter parameter value"
                  value={currentParam.value || ''}
                  valueType={currentParam.type}
                  onValueChange={(newValue) => {
                    form.setFieldValue(`inputParameters.${parameterName}`, {
                      ...currentParam,
                      value: newValue,
                    });
                  }}
                  onValueTypeChange={(newType) => {
                    if (newType === 'DYNAMIC' || newType === 'STATIC') {
                      form.setFieldValue(`inputParameters.${parameterName}`, {
                        ...currentParam,
                        type: newType,
                      });
                    }
                  }}
                  monacoInputProps={{
                    minLines: 1,
                    maxLines: 4,
                  }}
                  variables={testVariables}
                  onBlur={() => {
                    const { inputParameters } = form.getValues();
                    void updateComponentCallOp({
                      operationId,
                      update: { inputParameters },
                    });
                  }}
                  iconSizes={{
                    letterS: 14,
                    functionFilled: 16,
                  }}
                />
              </div>
            );
          })}
        </div>
      )}

      {!!hasOutputVars && (
        <div className="flex flex-col gap-1">
          <Text
            size="xs"
            fw={600}
          >
            Output Variables
          </Text>
          <div className="flex gap-2">
            {componentCallOp.component.outputVariables?.map((outputVar) => (
              <pre
                key={outputVar}
                className="rounded-md bg-gray-100 px-2 py-1 text-xs dark:bg-neutral-700/20"
              >
                {outputVar}
              </pre>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
